import { useCallback, useMemo } from "react"
import { UseFormReturn, useFormState, useWatch } from "react-hook-form"
import { useChBetterDocCareProviderSelection } from "@/modules/patients/detail/better-doc/ch/hooks/useChBetterDocCareProviderSelection"
import {
  type CareProviderSearchGuard,
  useBetterDocRejectionConfirmation,
} from "@/modules/patients/detail/better-doc/hooks/useBetterDocRejectionConfirmation"
import {
  type BetterDocUsageEvaluationOutcome,
  useBetterDocUsageForConsultation,
} from "@/modules/patients/detail/better-doc/hooks/useBetterDocUsageForConsultation"
import { getRecordedDiagnosisCode } from "@/modules/patients/detail/better-doc/lib/get-recorded-diagnosis-code"
import { isBetterDocAvailable } from "@/modules/patients/detail/better-doc/lib/is-better-doc-available"
import { getEffectivePointOfCareWithBetterDoc } from "@/modules/patients/detail/better-doc/lib/point-of-care-better-doc-rules"
import { toResolvedBetterDocUsage } from "@/modules/patients/detail/better-doc/lib/to-resolved-better-doc-usage"
import { useBetterDocSuggestionsStoreContext } from "@/modules/patients/detail/better-doc/store/useBetterDocSuggestionsStore"
import type { PointOfCareWithBetterDoc } from "@/modules/patients/detail/better-doc/types"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import {
  ACTUAL_POINT_OF_CARE_FIELD,
  MEDICAL_POINT_OF_CARE_FIELD,
} from "@/modules/patients/detail/progress-note/point-of-care/constants"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import type {
  BetterDoc,
  BetterDocConsultationDetail,
  BetterDocRejectionReason,
  Country,
  PointOfCareType,
} from "@/types/__generated__/union-types"

type UsePointOfCareBetterDocParameters = {
  form: UseFormReturn<PointOfCareFormType>
  countryCode: Country
  patientId: string
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  diagnosisCode: string
  preselectCareProviderForPointOfCare: (selectedPointOfCare: PointOfCareType | undefined) => void
  preselectCareProviderForSpecialty: (specialtyId: string | undefined) => void
}

export type PointOfCareBetterDoc = {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  effectivePointOfCareWithBetterDoc: PointOfCareWithBetterDoc | null
  betterDocUsage: BetterDoc | null
  missingConsultationDetailsThatCanChangeBetterDocUsage: ReadonlyArray<BetterDocConsultationDetail>
  isBetterDocUsagePending: boolean
  shouldShowBetterDocUsageFailure: boolean
  dismissBetterDocUsageFailure: () => void
  guardCareProviderSearch: CareProviderSearchGuard
  clearSelectedCareProvider: () => void
  clearBetterDocSuggestion: () => void
  isRejectionDialogOpen: boolean
  isConfirmingBetterDocRejection: boolean
  handleRejectionCancel: () => void
  handleRejectionConfirm: (betterDocRejectionReason: BetterDocRejectionReason) => void
  handleRejectionDialogCloseAutoFocus: (event: Event) => void
}

export const usePointOfCareBetterDoc = ({
  form,
  countryCode,
  patientId,
  episodeOfCareProgressNoteData,
  diagnosisCode,
  preselectCareProviderForPointOfCare,
  preselectCareProviderForSpecialty,
}: UsePointOfCareBetterDocParameters): PointOfCareBetterDoc => {
  const { control, getValues, setValue } = form
  const { isSubmitted } = useFormState({ control })
  const [medicalPointOfCare, actualPointOfCare, specialty] = useWatch({
    control,
    name: [MEDICAL_POINT_OF_CARE_FIELD, ACTUAL_POINT_OF_CARE_FIELD, "specialtyId"],
  })
  const effectivePointOfCareWithBetterDoc = getEffectivePointOfCareWithBetterDoc(medicalPointOfCare, actualPointOfCare)
  const progressNoteId = episodeOfCareProgressNoteData.id
  const isCareProviderSelectionAvailable = countryCode === "CH"

  const resetBetterDocSuggestions = useBetterDocSuggestionsStoreContext((store) => store.onReset)
  const clearBetterDocReferralLetterErrors = useProgressNoteFormContext(
    (state) => state.clearBetterDocReferralLetterErrors,
  )

  const clearBetterDocSuggestion = useCallback((): void => {
    setValue("isBetterDocSuggestion", false, { shouldDirty: true })
    setValue("betterDocSuggestionStatus", null)
    resetBetterDocSuggestions(progressNoteId)
    clearBetterDocReferralLetterErrors(progressNoteId)
  }, [clearBetterDocReferralLetterErrors, progressNoteId, resetBetterDocSuggestions, setValue])

  const { clearSelectedCareProvider, updateCareProviderAfterBetterDocUsageEvaluation } =
    useChBetterDocCareProviderSelection({
      form,
      effectivePointOfCareWithBetterDoc,
      specialty,
      clearBetterDocSuggestion,
      preselectCareProviderForPointOfCare,
      preselectCareProviderForSpecialty,
    })

  const writeResolvedBetterDocUsage = (betterDocUsage: BetterDoc | null): void => {
    const resolvedBetterDocUsage = toResolvedBetterDocUsage(betterDocUsage)
    if (resolvedBetterDocUsage === (getValues("resolvedBetterDocUsage") ?? null)) return
    setValue("resolvedBetterDocUsage", resolvedBetterDocUsage, { shouldDirty: isSubmitted })
  }

  const applyBetterDocUsageEvaluationOutcome = (
    betterDocUsageEvaluationOutcome: BetterDocUsageEvaluationOutcome,
  ): void => {
    if (betterDocUsageEvaluationOutcome.status === "FAILED") {
      writeResolvedBetterDocUsage(null)
      return
    }

    const { betterDocUsageEvaluation, previousSuccessfulEvaluation } = betterDocUsageEvaluationOutcome
    if (isCareProviderSelectionAvailable) {
      updateCareProviderAfterBetterDocUsageEvaluation(previousSuccessfulEvaluation, betterDocUsageEvaluation)
    }
    writeResolvedBetterDocUsage(betterDocUsageEvaluation.betterDocUsage)
  }

  const {
    betterDocUsage,
    missingConsultationDetailsThatCanChangeBetterDocUsage,
    isBetterDocUsagePending,
    shouldShowBetterDocUsageFailure,
    dismissBetterDocUsageFailure,
  } = useBetterDocUsageForConsultation({
    country: countryCode,
    patientId,
    diagnosisCode: getRecordedDiagnosisCode(episodeOfCareProgressNoteData, diagnosisCode),
    effectivePointOfCare: effectivePointOfCareWithBetterDoc,
    specialtyId: specialty?.id ?? null,
    enabled: isBetterDocAvailable(countryCode) && effectivePointOfCareWithBetterDoc !== null,
    onBetterDocUsageEvaluated: applyBetterDocUsageEvaluationOutcome,
  })

  const {
    guardCareProviderSearch,
    isRejectionDialogOpen,
    isConfirmingBetterDocRejection,
    handleRejectionCancel,
    handleRejectionConfirm,
    handleRejectionDialogCloseAutoFocus,
  } = useBetterDocRejectionConfirmation({
    form,
    countryCode,
    betterDocUsage,
    isBetterDocUsagePending,
    progressNoteId,
    confirmedBetterDocRejectionReason: episodeOfCareProgressNoteData.betterDoc?.rejectionReason,
  })

  return useMemo(
    () => ({
      episodeOfCareProgressNoteData,
      effectivePointOfCareWithBetterDoc,
      betterDocUsage,
      missingConsultationDetailsThatCanChangeBetterDocUsage,
      isBetterDocUsagePending,
      shouldShowBetterDocUsageFailure,
      dismissBetterDocUsageFailure,
      guardCareProviderSearch,
      clearSelectedCareProvider,
      clearBetterDocSuggestion,
      isRejectionDialogOpen,
      isConfirmingBetterDocRejection,
      handleRejectionCancel,
      handleRejectionConfirm,
      handleRejectionDialogCloseAutoFocus,
    }),
    [
      episodeOfCareProgressNoteData,
      effectivePointOfCareWithBetterDoc,
      betterDocUsage,
      missingConsultationDetailsThatCanChangeBetterDocUsage,
      isBetterDocUsagePending,
      shouldShowBetterDocUsageFailure,
      dismissBetterDocUsageFailure,
      guardCareProviderSearch,
      clearSelectedCareProvider,
      clearBetterDocSuggestion,
      isRejectionDialogOpen,
      isConfirmingBetterDocRejection,
      handleRejectionCancel,
      handleRejectionConfirm,
      handleRejectionDialogCloseAutoFocus,
    ],
  )
}
